/**
 * Enterprise Lesson 2: Live Audio/Video & Scoreboard Templates
 * Breadcrumb-only format (no voiceover). Reuses L1 step engine.
 */
import type { EnterpriseLessonDef } from '@graphicsim/shared-types';

export const entGfx02: EnterpriseLessonDef = {
  id: 'ent-gfx-02',
  name: 'Live Audio/Video & Scoreboard Templates',
  mode: 'steps',
  sections: [
    // Section 1: Live Camera Input (Blackmagic Decklink)
    {
      id: 'sec-1',
      title: 'Live Camera Input',
      steps: [
        {
          id: '1a',
          type: 'info',
          title: 'Live Audio/Video Overview',
          instruction: 'NEVCO Pro Video accepts live video from external devices. In this section, you will add a Blackmagic Decklink camera input and see it appear on the Program Monitor.',
        },
        {
          id: '1b',
          type: 'action',
          title: 'Add Blackmagic Decklink',
          instruction: 'Click File > New > Live Audio/Video > Blackmagic Decklink to add a live camera input.',
          highlightSelector: '#menubar',
          completionTrigger: {
            eventName: 'project:itemAdded',
            matchField: 'item',
            matchValue: 'contains:Decklink',
          },
          allowedSelectors: ['#menubar', '.menu-dropdown', '.menu-submenu'],
        },
        {
          id: '1c',
          type: 'info',
          title: 'Camera On Program',
          instruction: 'The Decklink camera is now in your Project panel and sending video to the Program Monitor automatically. Live inputs are always on — there is no need to Play them.',
          highlightSelector: '#panel-program',
        },
      ],
    },
    // Section 2: Football Scoreboard
    {
      id: 'sec-2',
      title: 'Football Scoreboard',
      steps: [
        {
          id: '2a',
          type: 'action',
          title: 'Open Football Folder',
          instruction: 'In the Graphics Library, click the Football folder to browse its templates.',
          highlightSelector: '#panel-library',
          completionTrigger: {
            eventName: 'library:folderSelected',
            matchField: 'folderId',
            matchValue: 'football',
          },
          allowedSelectors: ['#panel-library'],
        },
        {
          id: '2b',
          type: 'action',
          title: 'Add Scoreboard',
          instruction: 'Drag "Football Scoreboard" from the Library into the Project panel.',
          highlightSelector: '#panel-library',
          completionTrigger: {
            eventName: 'project:itemAdded',
            matchField: 'item',
            matchValue: 'contains:Football Scoreboard',
          },
          allowedSelectors: ['#panel-library', '#panel-project'],
          disabledSelectors: [
            '.lib-template[data-template-id="fb-bottom-scoreboard"]',
            '.lib-template[data-template-id="fb-halftime-stats"]',
          ],
        },
        {
          id: '2c',
          type: 'action',
          title: 'Edit Three Properties',
          instruction: 'Select the Scoreboard in the Project panel, then edit three different fields in the Properties panel — for example, change the team names and home score. When you are done, click Next.',
          highlightSelector: '#panel-properties',
          completionTrigger: {
            eventName: 'properties:fieldChanged',
            distinctField: 'fieldId',
            count: 3,
          },
          requireNextClick: true,
          allowedSelectors: ['#panel-project', '#panel-properties'],
        },
        {
          id: '2d',
          type: 'action',
          title: 'Take Scoreboard to Program',
          instruction: 'Click the Play button next to the Scoreboard in the Project panel to send it to the Program Monitor.',
          highlightSelector: '#panel-project',
          completionTrigger: {
            eventName: 'monitor:takeToProgram',
          },
          allowedSelectors: ['#panel-project', '.project-play-btn'],
        },
      ],
    },
    // Section 3: Halftime Stats
    {
      id: 'sec-3',
      title: 'Halftime Stats',
      steps: [
        {
          id: '3a',
          type: 'action',
          title: 'Add Halftime Stats',
          instruction: 'From the Football folder, drag "Football Halftime Stats" into the Project panel.',
          highlightSelector: '#panel-library',
          completionTrigger: {
            eventName: 'project:itemAdded',
            matchField: 'item',
            matchValue: 'contains:Halftime Stats',
          },
          allowedSelectors: ['#panel-library', '#panel-project'],
          disabledSelectors: [
            '.lib-template[data-template-id="fb-bottom-scoreboard"]',
            '.lib-template[data-template-id="fb-lower-third"]',
          ],
        },
        {
          id: '3b',
          type: 'action',
          title: 'Edit Three Stat Fields',
          instruction: 'Select the Halftime Stats template, then edit three different fields in the Properties panel — change any three stats or team names. When you are done, click Next.',
          highlightSelector: '#panel-properties',
          completionTrigger: {
            eventName: 'properties:fieldChanged',
            distinctField: 'fieldId',
            count: 3,
          },
          requireNextClick: true,
          allowedSelectors: ['#panel-project', '#panel-properties'],
        },
        {
          id: '3c',
          type: 'action',
          title: 'Take Scoreboard Off Air',
          instruction: 'Before showing Halftime Stats, take the Scoreboard off the Program Monitor. Click the Play button next to "Football Scoreboard" again to remove it from program.',
          highlightSelector: '#panel-project',
          completionTrigger: {
            eventName: 'monitor:removedFromProgram',
          },
          allowedSelectors: ['#panel-project', '.project-play-btn'],
        },
        {
          id: '3d',
          type: 'action',
          title: 'Take Halftime Stats to Program',
          instruction: 'Now click the Play button next to Halftime Stats to send it to the Program Monitor.',
          highlightSelector: '#panel-project',
          completionTrigger: {
            eventName: 'monitor:takeToProgram',
          },
          allowedSelectors: ['#panel-project', '.project-play-btn'],
        },
      ],
    },
    // Section 4: Wrap-up
    {
      id: 'sec-4',
      title: 'Review',
      steps: [
        {
          id: '4a',
          type: 'info',
          title: 'Well Done',
          instruction: 'You added a live camera input, a Football Scoreboard, and Halftime Stats — edited their fields and sent each to the Program Monitor. In the next lesson, you will learn to build Shots and Media Sequences.',
        },
      ],
    },
  ],
};
